import io from "socket.io-client";
import { parseDonationPayload } from "./parse-donation";
import type { Donation } from "./types";

const SOCKET_HOST = "wss://socket11.donationalerts.com:443";

export class DonationAlertsSocket {
  private _processedIds: string[] = [];
  private _socket: SocketIOClient.Socket;
  private _token: string;

  constructor(token: string) {
    this._token = token;
    this._socket = io(SOCKET_HOST, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 40000,
      reconnectionAttempts: Infinity,
      timeout: 30000,
    });

    this._socket.on("connect", () => {
      this._authenticate();
    });
    this._socket.on("reconnect", () => {
      this._authenticate();
    });
  }

  private _authenticate(): void {
    this._socket.emit("add-user", {
      token: this._token,
    });
  }

  /**
   * Listen for new donations
   */
  public onDonation(callback: (donation: Donation) => void): void {
    this._socket.on("donation", (message: string | Record<string, unknown>) => {
      if (message == null || message === "") {
        return;
      }

      try {
        const donation = parseDonationPayload(message);
        if (!donation) {
          return;
        }

        // Prevent duplicate processing
        if (this._processedIds.includes(donation.id)) {
          return;
        }

        callback(donation);

        this._processedIds.push(donation.id);
        // Keep the list size manageable
        if (this._processedIds.length > 100) {
          this._processedIds.shift();
        }
      } catch (error) {
        console.warn(`Failed to parse DonationAlerts message: ${message}`, error);
      }
    });
  }

  public onConnect(callback: () => void): void {
    const handler = () => {
      console.log("Connected to Donation Alerts");
      callback();
    };

    this._socket.on("connect", handler);
    if (this._socket.connected) {
      handler();
    }
  }

  public onError(callback: (error: Error) => void): void {
    this._socket.on("connect_error", (err: { message: string }) => {
      console.error(`Donation Alerts Connection Error: ${err.message}`);
      callback(new Error(err.message));
    });
  }

  public onDisconnect(callback: (reason: string) => void): void {
    this._socket.on("disconnect", (reason: string) => {
      console.log(`Donation Alerts Disconnected: ${reason}`);
      callback(reason);
    });
  }

  /**
   * Close the connection
   */
  public disconnect(): void {
    this._socket.disconnect();
  }
}
